import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { ChevronRight, Droplets, AlertTriangle, Coins } from "lucide-react";

/**
 * Public SEO page: tyre tread depth in New Zealand — legal minimum vs
 * wet-safety practice. No invented statistics; cites NZTA / Land
 * Transport Rule: Tyres and Wheels 2001. Soft CTA to /how-it-works +
 * quote flow.
 */
export default function TreadDepthPage() {
  const navigate = useNavigate();
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tyrematch.co.nz/" },
      { "@type": "ListItem", position: 2, name: "Tread depth NZ", item: "https://tyrematch.co.nz/tread-depth-nz" },
    ],
  };
  return (
    <div className="min-h-screen bg-background">
      <SEO page="treadDepth" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg text-primary" data-testid="tread-home-link">TyreMatch</Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/how-it-works" className="text-muted-foreground hover:text-primary">How it works</Link>
            <Button size="sm" onClick={() => navigate("/customer/auth")} data-testid="tread-cta-quote-btn">Get quotes</Button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <div>
          <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2">Tyre safety · New Zealand</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-4">Wet roads &amp; tyre tread depth in New Zealand</h1>
          <p className="text-lg text-muted-foreground">
            NZ's legal minimum tread is 1.5 mm across most of the tread pattern for a Warrant of Fitness. Wet-weather grip typically starts falling well before that — many NZ drivers replace at around 3 mm as a safety margin. Here's what to check and when.
          </p>
        </div>

        <section className="rounded-xl border-l-4 border-primary bg-primary/5 p-4">
          <p className="text-sm">
            <strong>Legal minimum:</strong> 1.5 mm across most of the tread pattern for most passenger tyres, under the <em>Land Transport Rule: Tyres and Wheels 2001</em> (enforced via WOF). Source: NZTA Waka Kotahi.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-bold mb-3 flex items-center gap-2"><Droplets className="h-5 w-5 text-primary" /> Why wet roads matter</h2>
          <p className="text-muted-foreground">
            On wet roads, the tread grooves have to clear water out from under the tyre so the rubber can stay in contact with the tarmac. When tread depth drops, the tyre can't clear as much water — grip falls and braking distances get longer. This effect starts gradually well before you hit the 1.5 mm legal minimum.
          </p>
        </section>

        {/* Depth guidance table */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">Simple tread-depth guide</h2>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2">Remaining tread</th>
                  <th className="text-left px-3 py-2">What it means in NZ</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr><td className="px-3 py-2 font-medium">~8 mm (new)</td><td className="px-3 py-2 text-muted-foreground">Full grip. Best water clearing.</td></tr>
                <tr><td className="px-3 py-2 font-medium">~3 mm</td><td className="px-3 py-2 text-muted-foreground">Common replace-soon threshold. Wet grip starts to drop noticeably. Plan a replacement.</td></tr>
                <tr><td className="px-3 py-2 font-medium">1.5 mm</td><td className="px-3 py-2 text-muted-foreground">NZ legal minimum for most passenger tyres. Any lower fails WOF.</td></tr>
                <tr><td className="px-3 py-2 font-medium">Below 1.5 mm</td><td className="px-3 py-2 text-red-600">Illegal to drive. Replace immediately.</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">3 mm is common industry safety guidance — <strong>not</strong> a second legal limit.</p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-bold mb-3 flex items-center gap-2"><Coins className="h-5 w-5 text-primary" /> How to check your tread</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li><strong>Tread wear indicators (TWIs):</strong> small raised bars inside the grooves. When the tread wears down flush with the TWIs, you're at 1.5 mm — replace now.</li>
            <li><strong>NZ 20c coin test:</strong> insert a 20c coin into the tread groove. If the outer band of the number "20" is visible, you're getting close to the wear indicators — time to plan a replacement.</li>
            <li><strong>Even wear:</strong> check across the width of the tyre, not just the centre. Uneven wear can point to an alignment issue.</li>
          </ul>
        </section>

        <section className="rounded-xl border p-4 flex items-start gap-3 bg-yellow-500/5 border-yellow-500/30">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
          <p className="text-sm">
            <strong>Coming up for a WOF?</strong> Get the tread checked before your booking — replacing before a WOF is usually cheaper than a re-check fee. If you're replacing, TyreMatch can help you get private quotes from local NZ shops.
          </p>
        </section>

        <section className="rounded-2xl bg-primary text-primary-foreground p-6 text-center">
          <h2 className="font-heading text-2xl font-bold mb-2">Time to replace? Get private quotes.</h2>
          <p className="text-primary-foreground/85 mb-4 text-sm">One request. Nearby NZ shops. Compare privately. <Link to="/how-it-works" className="underline">See how it works.</Link></p>
          <Button
            size="lg"
            onClick={() => navigate("/customer/auth")}
            className="bg-background text-foreground hover:bg-background/90 rounded-full"
            data-testid="tread-closing-cta"
          >
            Get Your Free Quotes Now
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </section>
      </main>

      <footer className="border-t py-6 text-center text-xs text-muted-foreground">
        <div className="max-w-3xl mx-auto px-4">
          Information here is a general guide only, based on the NZ Land Transport Rule: Tyres and Wheels 2001. Always check current WOF requirements at{" "}
          <a href="https://www.nzta.govt.nz/vehicles/warrants-and-certificates/warrant-of-fitness/" className="underline">NZTA Waka Kotahi</a>.
        </div>
      </footer>
    </div>
  );
}
